import type { ToolCall } from '@form-factory/schema';
import { ToolNames } from '@form-factory/schema/contracts';
import { ToolType } from '@form-factory/schema/enums';
import {
  type StreamStateStatus,
  isEnvelope,
  unwrapEnvelope,
  type ToolEnvelope,
} from '@form-factory/shared';
import { useMemo } from 'react';
import { z } from 'zod';

import type { ToolDefinition, ToolRegistry } from '../../toolRegistry';
import { Message } from '../ui';

/**
 * Represents the possible states of a tool throughout its lifecycle
 */
export type ToolState =
  | 'call' // Tool call generation started (LLM defining the tool call)
  | 'call-partial' // Tool is being defined, args are streaming
  | 'call-complete' // Tool definition is complete with full args and starts it's execution
  | 'result' // Tool has been executed with result
  | 'error'; // Error occurred during any phase

/**
 * Props for the tool component. All components
 * registered to the tool registry will receive
 * these props.
 */
export type ToolComponentProps<
  ToolCallType extends Record<string, any> = any,
  Content = unknown,
  Artifact = unknown,
  Props = {},
> = Props & {
  toolCall: ToolCall<ToolCallType>;
  state: ToolState;
  content?: Content;
  artifact?: Artifact;
  error?: string;
  parseError?: z.ZodError;
};

/**
 * Props for the tool provider component.
 */
export interface ToolProviderProps {
  toolName: string;
  toolCallId: string;
  args: unknown;
  content: unknown;
  artifact?: unknown;
  status: StreamStateStatus | undefined;
  toolRegistry: ToolRegistry;
  index: number;
  error?: string;
}

function getToolState(
  status: StreamStateStatus | undefined,
  content?: unknown,
  hasError?: boolean,
): ToolState {
  // Error state takes precedence
  if (hasError) {
    return 'error';
  }

  // Handle result case separately
  if (content) {
    return 'result';
  }

  // Map streaming status to tool state
  switch (status) {
    case 'start':
      return 'call';

    case 'streaming':
      return 'call-partial';

    case 'end':
      return 'call-complete';

    case 'error':
      return 'error';

    default:
      return 'call';
  }
}

/**
 * Type guard to check if a value is an object with a specific property
 */
function hasProperty<K extends string>(
  value: unknown,
  property: K,
): value is Record<K, unknown> {
  return typeof value === 'object' && value !== null && property in value;
}

/**
 * Helper to parse errors with schema.
 */
function parseWithSchema<T>(
  value: unknown,
  schema?: z.ZodType<T>,
): { data: T | unknown; error?: z.ZodError; isError?: boolean } {
  if (!schema || value === undefined) {
    return { data: value };
  }

  // Check for envelope error first
  if (isEnvelope(value) && !value.success) {
    return {
      data: undefined,
      isError: true,
      error: new z.ZodError([
        {
          code: 'custom',
          message: (value as ToolEnvelope).error || 'Tool execution failed',
          path: [],
        },
      ]),
    };
  }

  // Unwrap envelope for successful responses
  const unwrapped = unwrapEnvelope(value);

  if (unwrapped === undefined) {
    return { data: value, isError: true };
  }

  try {
    const result = schema.safeParse(unwrapped);

    if (result.success) {
      return { data: result.data };
    }

    console.error('Schema parsing error:', result.error);

    return { data: unwrapped, error: result.error };
  } catch (e) {
    console.error('Schema parsing error:', e);

    return { data: unwrapped };
  }
}

/**
 * Tool provider, renders the tool component with
 * the given state and result.
 */
export function ToolProvider({
  toolName,
  toolCallId,
  args,
  content,
  artifact,
  error: initialError,
  status,
  toolRegistry,
  index = 0,
}: ToolProviderProps) {
  // Find the tool definition in registry
  const toolDef =
    toolRegistry[toolName] ?? ({} as ToolDefinition<any, any, any>);
  const { validate, schema, onParseError } = toolDef;

  /**
   * Parse args, content, and artifact using schemas to make
   * sure the data are valid. Skip validation during streaming
   * states to avoid validation errors on incomplete data.
   */
  const isStreaming = status === 'start' || status === 'streaming';
  const shouldValidate = validate && !isStreaming;

  const parsedArgs = useMemo(
    () =>
      shouldValidate ? parseWithSchema(args, schema?.args) : { data: args },
    [args, schema?.args, shouldValidate],
  );

  const parsedContent = useMemo(() => {
    if (shouldValidate) {
      // parseWithSchema handles envelope unwrapping
      return parseWithSchema(content, schema?.content);
    } else {
      // For non-validated tools, we need to unwrap envelopes manually
      // to ensure consistent behavior
      const unwrapped = isEnvelope(content) ? unwrapEnvelope(content) : content;

      return { data: unwrapped };
    }
  }, [content, schema?.content, shouldValidate]);

  const parsedArtifact = useMemo(
    () =>
      shouldValidate
        ? parseWithSchema(artifact, schema?.artifact)
        : { data: artifact },
    [artifact, schema?.artifact, shouldValidate],
  );

  // Generate parsing error message if needed
  const parseError = useMemo(() => {
    if (!shouldValidate) {
      return;
    }

    const errors = [
      parsedArgs.error,
      parsedContent.error,
      parsedArtifact.error,
    ].filter(Boolean);

    if (errors.length === 0) {
      return;
    }

    if (onParseError) {
      return errors.map(error => onParseError!(error!)).join('\n');
    }

    // Default error message
    return 'Failed to parse tool response data';
  }, [
    shouldValidate,
    parsedArgs.error,
    parsedContent.error,
    parsedArtifact.error,
    onParseError,
  ]);

  // Create a standardized tool call object
  const toolCall: ToolCall<any> = useMemo(
    () => ({
      id: toolCallId,
      type: ToolType.Tool,
      name: toolName,
      args: parsedArgs.data,
      index,
      content: parsedContent.data,
      artifact: parsedArtifact.data,
    }),
    [
      toolCallId,
      toolName,
      parsedArgs.data,
      index,
      parsedContent.data,
      parsedArtifact.data,
    ],
  );

  if (!Object.keys(toolDef).length) {
    // Tools that are handled by the agent backend don't need UI representation
    // These tools return data to the agent which formulates the response
    if (toolName === ToolNames.DocumentSearch) {
      return null;
    }

    // For other unknown tools, show fallback content if available
    let fallbackContent: unknown = parsedContent.data;

    if (!fallbackContent && hasProperty(parsedArgs.data, 'content')) {
      fallbackContent = parsedArgs.data.content;
    }

    if (!fallbackContent && hasProperty(parsedArgs.data, 'message')) {
      fallbackContent = parsedArgs.data.message;
    }

    // Only show fallback if we have meaningful content to display
    // Don't show the generic "Processing your request..." message
    if (
      typeof fallbackContent === 'string' &&
      fallbackContent.trim() &&
      fallbackContent !== 'Processing your request...'
    ) {
      return (
        <Message.BubbleGroup>
          <Message.Bubble>{fallbackContent}</Message.Bubble>
        </Message.BubbleGroup>
      );
    }

    return null;
  }

  // Determine final state - error state takes precedence
  const hasError = Boolean(
    initialError ||
      parseError ||
      parsedContent.isError ||
      parsedArgs.isError ||
      parsedArtifact.isError,
  );
  const finalState = getToolState(status, content, hasError);

  // Combine all error messages
  const error =
    initialError ||
    parseError ||
    (parsedContent.isError ? 'Tool execution failed' : undefined) ||
    (parsedArgs.isError ? 'Invalid tool arguments' : undefined) ||
    (parsedArtifact.isError ? 'Invalid tool artifact' : undefined);

  const { component: ToolComponent } = toolDef;

  return (
    <ToolComponent
      toolCall={toolCall}
      state={finalState}
      content={parsedContent.data}
      artifact={parsedArtifact.data}
      error={error}
      parseError={
        parsedArgs.error || parsedContent.error || parsedArtifact.error
      }
    />
  );
}
